import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { DatePicker } from '../components/ui/DatePicker';
import { Checkbox } from '../components/ui/Checkbox';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { CustomerSelect } from '../components/domain/CustomerSelect';
import { LineItemRow } from '../components/domain/LineItemRow';
import { TotalsPanel } from '../components/domain/TotalsPanel';
import { QuoteApprovalModal } from '../components/domain/QuoteApprovalModal';
import { callBillingAPI, callWFAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { calculateSubtotal, calculateTaxAmount, calculateInvoiceTotal, calculateProfitMargin } from '../utils/calculations';
import { LineItem, InvoiceStatus } from '../types';
import { ArrowLeftIcon, PlusIcon, SaveIcon, SendIcon, CreditCardIcon, CheckCircleIcon, AlertCircleIcon, CalendarIcon, UserIcon, RefreshCwIcon, FileTextIcon, MailIcon, PhoneIcon, MapPinIcon, TrashIcon, XCircleIcon, SettingsIcon, ExternalLinkIcon, PencilIcon, CopyIcon } from 'lucide-react';
import { Modal } from '../components/ui/Modal';
import { INVOICE_FILTERS_STORAGE_KEY } from './Invoices';
// Storage key for caching WF order filters - now per company
const WF_ORDER_FILTERS_CACHE_KEY_PREFIX = 'wfOrderFiltersCache_';
// Cache interface
interface WFOrderFiltersCache {
  companyId: string;
  timestamp: number;
  statusFilters: Array<{
    id: string;
    name: string;
  }>;
}
interface LineItemFormData {
  description: string;
  quantity: number;
  price: number;
  jobCost?: number;
  isTaxable: boolean;
}
// API invoice item type
interface ApiInvoiceItem {
  title: string;
  qty: number;
  price: number;
  amount: number;
  taxable?: number;
}
// API invoice response type
interface ApiInvoiceResponse {
  id: string;
  number: string;
  items: ApiInvoiceItem[];
  amount: number;
  paid_amount: number;
  tax: number;
  tax_label?: string;
  fee?: number;
  fee_label?: string;
  shipping_amount: number;
  discount: number;
  status: string;
  created: number;
  sent_date?: number;
  invoice_date?: number;
  customer_id: string;
  customer_name: string;
  customer_organization?: string;
  customer?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    organization?: string;
    address_street?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
  };
  source?: string;
  subscription_id?: string;
  subscription_number?: string;
  payment_period_days: number | string;
  payment_date?: number;
  payment_method?: string;
  notes?: string;
  send_options?: string;
  link?: string;
  extra?: {
    wf_order_id?: string;
    wf_status_id?: string; // WF order status ID lives inside extra
    wf_ship_methods?: string[]; // WF shipping methods
    [key: string]: any;
  };
}
export function InvoiceForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const location = useLocation();
  const isExistingInvoice = !!id;
  // Check if we're on the edit route (for draft/future invoices)
  const isEditMode = location.pathname.endsWith('/edit');
  // Show view mode only for existing invoices that are NOT in edit mode
  const isViewMode = isExistingInvoice && !isEditMode;
  const {
    createInvoice,
    updateInvoice,
    getInvoiceById,
    getLineItemsByInvoiceId,
    selectedCompanyId,
    selectedCompany,
    taxRate
  } = useData();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showQuoteApprovalModal, setShowQuoteApprovalModal] = useState(false);
  // Re-send invoice modal state
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendViaEmail, setResendViaEmail] = useState(true);
  const [resendViaSMS, setResendViaSMS] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  // Delete invoice modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isCheckingPayments, setIsCheckingPayments] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  // Mark as paid state
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [paidVia, setPaidVia] = useState('');
  const [paidNotes, setPaidNotes] = useState('');
  const [markPaidError, setMarkPaidError] = useState<string | null>(null);
  // Void invoice state
  const [isVoiding, setIsVoiding] = useState(false);
  // Customer Payment Methods state
  const [customerPaymentMethods, setCustomerPaymentMethods] = useState<any[]>([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  // Save & Pay Modal state
  const [showSaveAndPayModal, setShowSaveAndPayModal] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>('');
  // Handle WF Order Status Update
  const handleUpdateWfStatus = async () => {
    if (!selectedCompany?.wf_api_info || !invoiceData?.extra?.wf_order_id || !selectedWfStatusId) return;
    setIsUpdatingWfStatus(true);
    setWfStatusError(null);
    try {
      await callWFAPI(selectedCompany.wf_api_info.login, selectedCompany.wf_api_info.key, selectedCompany.wf_api_info.ecomModuleId, 'changeOrders', {
        order: {
          id: invoiceData.extra.wf_order_id,
          status: {
            new: selectedWfStatusId,
            message: wfStatusMessage.trim() || undefined
          }
        }
      });
      setWfStatusMessage('');
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Failed to update WF order status:', error);
      setWfStatusError(error instanceof Error ? error.message : 'Failed to update order status');
    } finally {
      setIsUpdatingWfStatus(false);
    }
  };
  // Payments modal state
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  // Invoice data from API (for viewing)
  const [invoiceData, setInvoiceData] = useState<ApiInvoiceResponse | null>(null);
  // WF Order Filters state
  const [wfOrderFilters, setWfOrderFilters] = useState<Array<{
    id: string;
    name: string;
  }> | null>(null);
  // WF Order Status Update state
  const [selectedWfStatusId, setSelectedWfStatusId] = useState<string>('');
  const [showWfStatusModal, setShowWfStatusModal] = useState(false);
  const [wfStatusMessage, setWfStatusMessage] = useState('');
  const [isUpdatingWfStatus, setIsUpdatingWfStatus] = useState(false);
  const [wfStatusError, setWfStatusError] = useState<string | null>(null);
  // Form state
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerOrganization, setCustomerOrganization] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceSource, setInvoiceSource] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [sentDate, setSentDate] = useState<Date | null>(null);
  const [paymentDate, setPaymentDate] = useState<Date | null>(null);
  const [createdDate, setCreatedDate] = useState<Date | null>(null);
  const [invoiceDateDisplay, setInvoiceDateDisplay] = useState<Date | null>(null);
  const [status, setStatus] = useState<string>('draft');
  // Check if customer should be locked (non-draft invoices being edited)
  const isCustomerLocked = isEditMode && (status === 'sent' || status === 'overdue');
  const [shippingCharge, setShippingCharge] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [fee, setFee] = useState(0);
  const [sendViaEmail, setSendViaEmail] = useState(true);
  const [sendViaSMS, setSendViaSMS] = useState(false);
  const [paymentPeriodDays, setPaymentPeriodDays] = useState(0);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [subscriptionNumber, setSubscriptionNumber] = useState<string | null>(null);
  const [invoiceLink, setInvoiceLink] = useState<string | null>(null);
  const [paidAmount, setPaidAmount] = useState(0);
  const [invoicePaymentMethod, setInvoicePaymentMethod] = useState<string | null>(null);
  const [invoiceNotes, setInvoiceNotes] = useState<string | null>(null);
  const [storedAmount, setStoredAmount] = useState(0);
  const [storedTax, setStoredTax] = useState(0);
  const [storedTaxLabel, setStoredTaxLabel] = useState<string | undefined>(undefined);
  const [storedFee, setStoredFee] = useState(0);
  const [storedFeeLabel, setStoredFeeLabel] = useState<string | undefined>(undefined);
  // Extra settings for labels
  const [taxLineLabel, setTaxLineLabel] = useState('');
  const [feeLineLabel, setFeeLineLabel] = useState('Fee');
  const [lineItems, setLineItems] = useState<LineItemFormData[]>([{
    description: '',
    quantity: 1,
    price: 0,
    isTaxable: true
  }]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Get today's date for minimum date restriction
  const today = new Date().toISOString().split('T')[0];
  // Check if selected date is in the future (not today)
  const isFutureDate = useMemo(() => {
    if (!date) return false;
    const selectedDate = new Date(date);
    const todayDate = new Date(today);
    // Compare dates without time
    selectedDate.setHours(0, 0, 0, 0);
    todayDate.setHours(0, 0, 0, 0);
    return selectedDate > todayDate;
  }, [date, today]);
  // Sync search input with URL param on mount
  /*useEffect(() => {
    setSearchInput(searchQuery)
  }, [])*/
  // Fetch WF order filters when company has WF integration enabled
  useEffect(() => {
    const fetchWFOrderFilters = async () => {
      if (!selectedCompany || !selectedCompanyId) return;
      // Check if company has WF orders enabled
      if (selectedCompany.use_wf_orders !== 1 || !selectedCompany.wf_api_info) {
        return;
      }
      // Use per-company cache key
      const cacheKey = `${WF_ORDER_FILTERS_CACHE_KEY_PREFIX}${selectedCompanyId}`;
      // Check cache first
      try {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          const cache: WFOrderFiltersCache = JSON.parse(cachedData);
          setWfOrderFilters(cache.statusFilters);
          return;
        }
      } catch (e) {
        console.error('Failed to read WF cache:', e);
      }
      // Fetch from API if not cached
      try {
        const wfApiInfo = selectedCompany.wf_api_info;
        const wfFilters = await callWFAPI(wfApiInfo.login, wfApiInfo.key, wfApiInfo.ecomModuleId, 'getOrderFilters');
        const statusFilters = wfFilters.status_filters || [];
        setWfOrderFilters(statusFilters);
        // Cache the result with per-company key
        const cacheData: WFOrderFiltersCache = {
          companyId: selectedCompanyId,
          timestamp: Date.now(),
          statusFilters: statusFilters
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (err) {
        console.error('Failed to fetch WF order filters:', err);
      }
    };
    fetchWFOrderFilters();
  }, [selectedCompany, selectedCompanyId]);
  // Helper function to filter and format WF order status options
  const getFilteredWfStatusOptions = useCallback(() => {
    if (!wfOrderFilters) return [];
    return wfOrderFilters.filter(filter => {
      // Only show statuses with numeric IDs
      const numericId = parseInt(filter.id);
      return !isNaN(numericId);
    }).map(filter => {
      const numericId = parseInt(filter.id);
      // If ID is zero or negative, enclose label with < and >
      const label = numericId <= 0 ? `<${filter.name}>` : filter.name;
      return {
        value: filter.id,
        label: label
      };
    });
  }, [wfOrderFilters]);
  // Load existing invoice data from API
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!isExistingInvoice || !id) return;
      setIsLoadingInvoice(true);
      setLoadError(null);
      try {
        console.log('Fetching invoice:', id);
        const response = await callBillingAPI('getInvoice', {
          id
        });
        console.log('Invoice API response:', response);
        setInvoiceData(response);
        // Populate form fields
        setCustomerId(response.customer_id || '');
        setCustomerName(response.customer_name || '');
        setCustomerOrganization(response.customer_organization || '');
        setCustomerEmail(response.customer_email || '');
        setCustomerPhone(response.customer_phone || '');
        setCustomerAddress(response.customer_address || '');
        // Override with customer object data if available
        if (response.customer && Object.keys(response.customer).length > 0) {
          const c = response.customer;
          const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ');
          if (fullName) setCustomerName(fullName);
          if (c.organization) setCustomerOrganization(c.organization);
          if (c.email) setCustomerEmail(c.email);
          if (c.phone) setCustomerPhone(c.phone);
          const addressParts = [c.address_street, c.address_city, c.address_state, c.address_zip].filter(Boolean);
          if (addressParts.length > 0) setCustomerAddress(addressParts.join(', '));
        }
        setInvoiceNumber(response.number || '');
        setInvoiceSource(response.source || '');
        setStatus(response.status || 'draft');
        setShippingCharge(parseFloat(response.shipping_amount) || 0);
        setDiscount(parseFloat(response.discount) || 0);
        setFee(parseFloat(response.fee) || 0);
        const parsedPeriodDays = parseInt(String(response.payment_period_days));
        setPaymentPeriodDays(isNaN(parsedPeriodDays) ? 30 : parsedPeriodDays);
        setPaidAmount(parseFloat(response.paid_amount) || 0);
        setStoredAmount(parseFloat(response.amount) || 0);
        setStoredTax(parseFloat(response.tax) || 0);
        setStoredTaxLabel(response.tax_label || undefined);
        setStoredFee(parseFloat(response.fee) || 0);
        setStoredFeeLabel(response.fee_label || undefined);
        // Set label fields from API response
        if (response.tax_label) {
          setTaxLineLabel(response.tax_label);
        }
        if (response.fee_label) {
          setFeeLineLabel(response.fee_label);
        }
        if (response.created) {
          setCreatedDate(new Date(response.created * 1000));
        }
        // Set the date field from invoice_date (priority) or fallback to created date
        if (response.invoice_date) {
          const invoiceDateObj = new Date(response.invoice_date * 1000);
          setDate(invoiceDateObj.toISOString().split('T')[0]);
          setInvoiceDateDisplay(invoiceDateObj);
        } else if (response.created) {
          setDate(new Date(response.created * 1000).toISOString().split('T')[0]);
        }
        if (response.sent_date) {
          setSentDate(new Date(response.sent_date * 1000));
        }
        if (response.payment_date) {
          const pd = Number(response.payment_date);
          if (pd > 0) {
            setPaymentDate(new Date(pd * 1000));
          }
        }
        if (response.subscription_id) {
          setSubscriptionId(response.subscription_id);
          setSubscriptionNumber(response.subscription_number || null);
        }
        if (response.link) {
          setInvoiceLink(response.link);
        }
        if (response.payment_method) {
          setInvoicePaymentMethod(response.payment_method);
        }
        if (response.notes) {
          setInvoiceNotes(response.notes);
        }
        // Set send options based on API response
        if (response.send_options) {
          const sendOptions = response.send_options;
          if (sendOptions === 'email') {
            setSendViaEmail(true);
            setSendViaSMS(false);
          } else if (sendOptions === 'sms') {
            setSendViaEmail(false);
            setSendViaSMS(true);
          } else if (sendOptions === 'email_sms') {
            setSendViaEmail(true);
            setSendViaSMS(true);
          }
        }
        // Transform items
        if (response.items && Array.isArray(response.items)) {
          const transformedItems: LineItemFormData[] = response.items.map((item: ApiInvoiceItem) => ({
            description: item.title,
            quantity: parseFloat(String(item.qty)) || 1,
            price: parseFloat(String(item.price)) || 0,
            isTaxable: item.taxable === 1
          }));
          setLineItems(transformedItems.length > 0 ? transformedItems : [{
            description: '',
            quantity: 1,
            price: 0,
            isTaxable: true
          }]);
        }
      } catch (error) {
        console.error('Failed to fetch invoice:', error);
        setLoadError(error instanceof Error ? error.message : 'Failed to load invoice');
      } finally {
        setIsLoadingInvoice(false);
      }
    };
    fetchInvoice();
  }, [isExistingInvoice, id]);
  // Fetch customer details (email, phone, address) when customer_id is available
  // Only needed as fallback if the invoice response didn't include a customer object
  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!customerId || !isExistingInvoice) return;
      // Skip if we already have customer data from the invoice response
      if (invoiceData?.customer && Object.keys(invoiceData.customer).length > 0) return;
      try {
        const customer = await callBillingAPI('getCustomer', {
          id: customerId
        });
        if (customer) {
          setCustomerEmail(customer.email || '');
          setCustomerPhone(customer.phone || '');
          // Build address string from parts
          const addressParts = [customer.address_street, customer.address_city, customer.address_state, customer.address_zip].filter(Boolean);
          setCustomerAddress(addressParts.join(', '));
        }
      } catch (err) {
        console.error('Failed to fetch customer details:', err);
      }
    };
    fetchCustomerDetails();
  }, [customerId, isExistingInvoice, invoiceData]);
  // Fetch customer payment methods when customerId changes (only in create mode)
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (isExistingInvoice || !customerId) {
        setCustomerPaymentMethods([]);
        return;
      }
      setIsLoadingPaymentMethods(true);
      try {
        const response = await callBillingAPI('getCustomerPaymentMethods', {
          customer_id: customerId
        });
        let methods: any[] = [];
        if (Array.isArray(response)) {
          methods = response;
        } else if (response.data && Array.isArray(response.data)) {
          methods = response.data;
        }
        setCustomerPaymentMethods(methods);
        if (methods.length > 0) {
          setSelectedPaymentMethodId(methods.id || methods[0].id);
        } else {
          setSelectedPaymentMethodId('');
        }
      } catch (err) {
        console.error('Failed to fetch payment methods:', err);
        setCustomerPaymentMethods([]);
      } finally {
        setIsLoadingPaymentMethods(false);
      }
    };
    fetchPaymentMethods();
  }, [customerId, isExistingInvoice]);
  // Calculate totals
  const calculatedLineItems: LineItem[] = useMemo(() => {
    return lineItems.map((item, index) => ({
      id: `temp-${index}`,
      invoice_id: id || 'new',
      description: item.description,
      quantity: item.quantity,
      price: item.price,
      job_cost: item.jobCost,
      is_taxable: item.isTaxable,
      profit_margin: calculateProfitMargin(item.price, item.jobCost)
    }));
  }, [lineItems, id]);
  // Calculate subtotal from line items (works for both new and existing invoices)
  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const qty = parseFloat(String(item.quantity)) || 0;
      const price = parseFloat(String(item.price)) || 0;
      return sum + qty * price;
    }, 0);
  }, [lineItems]);
  // Calculate taxable subtotal (only items marked as taxable)
  const taxableSubtotal = useMemo(() => {
    return lineItems.filter(item => item.isTaxable).reduce((sum, item) => {
      const qty = parseFloat(String(item.quantity)) || 0;
      const price = parseFloat(String(item.price)) || 0;
      return sum + qty * price;
    }, 0);
  }, [lineItems]);
  const taxAmount = useMemo(() => isViewMode ? storedTax : calculateTaxAmount(calculatedLineItems, taxRate), [isViewMode, storedTax, calculatedLineItems, taxRate]);
  const total = useMemo(() => isViewMode ? storedAmount : calculateInvoiceTotal(parseFloat(String(subtotal)) || 0, parseFloat(String(taxAmount)) || 0, parseFloat(String(shippingCharge)) || 0, parseFloat(String(discount)) || 0) + (parseFloat(String(fee)) || 0), [isViewMode, storedAmount, subtotal, taxAmount, shippingCharge, discount, fee]);
  const balanceDue = useMemo(() => total - paidAmount, [total, paidAmount]);
  // Get default tax label based on company tax rate
  const getDefaultTaxLabel = useCallback(() => {
    const taxPercent = taxRate * 100;
    if (taxPercent > 0) {
      return `Tax (${taxPercent.toFixed(0)}%)`;
    }
    return 'Tax';
  }, [taxRate]);
  // Initialize default tax label when creating new invoice
  useEffect(() => {
    if (!isExistingInvoice && !taxLineLabel) {
      setTaxLineLabel(getDefaultTaxLabel());
    }
  }, [isExistingInvoice, taxLineLabel, getDefaultTaxLabel]);
  // Pre-populate customer from navigation state (e.g., from Customer View page or Clone)
  useEffect(() => {
    if (!isExistingInvoice && location.state) {
      const state = location.state as {
        customerId?: string;
        customerName?: string;
        cloneData?: {
          customerId: string;
          customerName: string;
          lineItems: LineItemFormData[];
          shippingCharge: number;
          discount: number;
          fee: number;
          paymentPeriodDays: number;
          sendViaEmail: boolean;
          sendViaSMS: boolean;
          taxLineLabel: string;
          feeLineLabel: string;
        };
      };
      if (state.cloneData) {
        // Clone mode: pre-populate all fields from source invoice
        const c = state.cloneData;
        setCustomerId(c.customerId);
        setCustomerName(c.customerName);
        setLineItems(c.lineItems.length > 0 ? c.lineItems : [{
          description: '',
          quantity: 1,
          price: 0,
          isTaxable: true
        }]);
        setShippingCharge(c.shippingCharge);
        setDiscount(c.discount);
        setFee(c.fee);
        setPaymentPeriodDays(c.paymentPeriodDays);
        setSendViaEmail(c.sendViaEmail);
        setSendViaSMS(c.sendViaSMS);
        if (c.taxLineLabel) setTaxLineLabel(c.taxLineLabel);
        if (c.feeLineLabel) setFeeLineLabel(c.feeLineLabel);
        // Explicitly set date to today and clear invoice number for the clone
        setDate(new Date().toISOString().split('T')[0]);
        setInvoiceNumber('');
      } else {
        if (state.customerId) {
          setCustomerId(state.customerId);
        }
        if (state.customerName) {
          setCustomerName(state.customerName);
        }
      }
    }
  }, [isExistingInvoice, location.state]);
  const addLineItem = () => {
    setLineItems([...lineItems, {
      description: '',
      quantity: 1,
      price: 0,
      isTaxable: true
    }]);
  };
  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };
  const updateLineItem = (index: number, updates: Partial<LineItemFormData>) => {
    setLineItems(lineItems.map((item, i) => i === index ? {
      ...item,
      ...updates
    } : item));
  };
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!customerId) {
      newErrors.customer = 'Please select a customer';
    }
    if (!date) {
      newErrors.date = 'Please select a date';
    }
    if (lineItems.length === 0) {
      newErrors.lineItems = 'Please add at least one line item';
    }
    const hasEmptyLineItem = lineItems.some(item => !item.description.trim() || item.price < 0);
    if (hasEmptyLineItem) {
      newErrors.lineItems = 'Please fill in all line item details';
    }
    // Require at least one send option
    if (!sendViaEmail && !sendViaSMS) {
      newErrors.sendOptions = 'Please select at least one send option';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = async (sendAfterSave: boolean = false, payOptions?: {
    paymentMethodId: string;
  }) => {
    if (!validate()) return;
    setIsLoading(true);
    try {
      if (isExistingInvoice && id) {
        // Update existing invoice via API
        const items = lineItems.map(item => ({
          title: item.description,
          qty: item.quantity,
          price: item.price,
          amount: item.quantity * item.price,
          taxable: item.isTaxable ? 1 : 0
        }));
        // Format date as mm-dd-yyyy
        const dateObj = new Date(date);
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const year = dateObj.getFullYear();
        const formattedInvoiceDate = `${month}-${day}-${year}`;
        // Determine send_options value
        let sendOptions: string;
        if (sendViaEmail && sendViaSMS) {
          sendOptions = 'email_sms';
        } else if (sendViaEmail) {
          sendOptions = 'email';
        } else {
          sendOptions = 'sms';
        }
        const apiParams: Record<string, any> = {
          id: id,
          customer_id: customerId,
          items: items,
          total_amount: total,
          tax: {
            amount: taxAmount,
            label: taxLineLabel || getDefaultTaxLabel()
          },
          fee: {
            amount: fee,
            label: feeLineLabel || 'Fee'
          },
          shipping_amount: shippingCharge,
          discount: discount,
          term_days: paymentPeriodDays,
          invoice_date: formattedInvoiceDate,
          send_options: sendOptions
        };
        // Add send flag if "Save & Send" was clicked
        if (sendAfterSave) {
          apiParams.send = 1;
        }
        // Add pay options if provided
        if (payOptions) {
          apiParams.pay_invoice = 1;
          apiParams.customer_payment_method_id = payOptions.paymentMethodId;
          apiParams.delete_invoice_on_payment_fail = 1;
        }
        console.log('Updating invoice with params:', apiParams);
        const updatedInvoice = await callBillingAPI('editInvoice', apiParams);
        console.log('Update invoice response:', updatedInvoice);
      } else {
        // Create new invoice via API
        const items = lineItems.map(item => ({
          title: item.description,
          qty: item.quantity,
          price: item.price,
          amount: item.quantity * item.price,
          taxable: item.isTaxable ? 1 : 0
        }));
        // Format date as mm-dd-yyyy
        const dateObj = new Date(date);
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const year = dateObj.getFullYear();
        const formattedInvoiceDate = `${month}-${day}-${year}`;
        // Determine send_options value
        let sendOptions: string;
        if (sendViaEmail && sendViaSMS) {
          sendOptions = 'email_sms';
        } else if (sendViaEmail) {
          sendOptions = 'email';
        } else {
          sendOptions = 'sms';
        }
        const apiParams: Record<string, any> = {
          company_id: selectedCompanyId,
          customer_id: customerId,
          items: items,
          total_amount: total,
          tax: {
            amount: taxAmount,
            label: taxLineLabel || getDefaultTaxLabel()
          },
          fee: {
            amount: fee,
            label: feeLineLabel || 'Fee'
          },
          shipping_amount: shippingCharge,
          discount: discount,
          term_days: paymentPeriodDays,
          invoice_date: formattedInvoiceDate,
          send_options: sendOptions
        };
        // Add send flag if "Save & Send" was clicked
        if (sendAfterSave) {
          apiParams.send = 1;
        }
        // Add pay options if provided
        if (payOptions) {
          apiParams.pay_invoice = 1;
          apiParams.customer_payment_method_id = payOptions.paymentMethodId;
          apiParams.delete_invoice_on_payment_fail = 1;
        }
        console.log('Creating invoice with params:', apiParams);
        const newInvoice = await callBillingAPI('createInvoice', apiParams);
        console.log('Create invoice response:', newInvoice);
        const newInvoiceId = newInvoice.id;
        console.log('New invoice ID:', newInvoiceId);
      }
      navigate('/invoices');
    } catch (error) {
      console.error('Failed to save invoice:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to save invoice'
      });
    } finally {
      setIsLoading(false);
    }
  };
  // Get status badge
  const getStatusBadge = (statusValue: string) => {
    const statusConfig: Record<string, {
      variant: 'success' | 'warning' | 'danger' | 'info' | 'default';
      label: string;
    }> = {
      draft: {
        variant: 'default',
        label: 'Draft'
      },
      sent: {
        variant: 'info',
        label: 'Sent'
      },
      paid: {
        variant: 'success',
        label: 'Paid'
      },
      partial_paid: {
        variant: 'warning',
        label: 'Partial Paid'
      },
      overdue: {
        variant: 'danger',
        label: 'Overdue'
      },
      voided: {
        variant: 'default',
        label: 'Voided'
      },
      future: {
        variant: 'info',
        label: 'Future'
      }
    };
    const config = statusConfig[statusValue] || {
      variant: 'default' as const,
      label: statusValue
    };
    return <Badge variant={config.variant} data-id="element-1301">{config.label}</Badge>;
  };
  // Format date
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Handle re-send invoice
  const handleResendInvoice = async () => {
    if (!resendViaEmail && !resendViaSMS) {
      setResendError('Please select at least one send option');
      return;
    }
    if (!id) return;
    setIsResending(true);
    setResendError(null);
    try {
      // Determine send_options value
      let sendOptions: string;
      if (resendViaEmail && resendViaSMS) {
        sendOptions = 'email_sms';
      } else if (resendViaEmail) {
        sendOptions = 'email';
      } else {
        sendOptions = 'sms';
      }
      await callBillingAPI('sendInvoice', {
        id: id,
        send_options: sendOptions
      });
      setShowResendModal(false);
      // Optionally refresh the page or show success message
      window.location.reload();
    } catch (error) {
      console.error('Failed to re-send invoice:', error);
      setResendError(error instanceof Error ? error.message : 'Failed to re-send invoice');
    } finally {
      setIsResending(false);
    }
  };
  // Open re-send modal with current send options pre-selected
  const openResendModal = () => {
    setResendViaEmail(sendViaEmail);
    setResendViaSMS(sendViaSMS);
    setResendError(null);
    setShowResendModal(true);
  };
  // Handle delete invoice
  const handleDeleteInvoice = async () => {
    if (!id) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await callBillingAPI('deleteInvoice', {
        id: id
      });
      setShowDeleteModal(false);
      navigate('/invoices');
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete invoice');
    } finally {
      setIsDeleting(false);
    }
  };
  // Check for payments before allowing delete
  const handleDeleteClick = async () => {
    if (!id) return;
    setIsCheckingPayments(true);
    setDeleteBlocked(false);
    setDeleteConfirmText('');
    setDeleteError(null);
    try {
      const response = await callBillingAPI('getPayments', {
        invoice_id: id
      });
      let paymentsList: any[] = [];
      if (Array.isArray(response)) {
        paymentsList = response;
      } else if (response.payments && Array.isArray(response.payments)) {
        paymentsList = response.payments;
      } else if (response.data && Array.isArray(response.data)) {
        paymentsList = response.data;
      }
      // Block deletion only if there are successful payments
      const hasSuccessfulPayment = paymentsList.some((p: any) => p.status === 'success');
      if (hasSuccessfulPayment) {
        setDeleteBlocked(true);
      }
      setShowDeleteModal(true);
    } catch (err) {
      // If we can't check payments, still allow opening the modal
      console.error('Failed to check payments:', err);
      setShowDeleteModal(true);
    } finally {
      setIsCheckingPayments(false);
    }
  };
  // Handle mark invoice as paid
  const handleMarkAsPaid = async () => {
    if (!id) return;
    if (!paidVia) {
      setMarkPaidError('Please select how the invoice was paid');
      return;
    }
    setIsMarkingPaid(true);
    setMarkPaidError(null);
    try {
      await callBillingAPI('markInvoicePaid', {
        id: id,
        payment_method: paidVia,
        notes: paidNotes.trim() || undefined
      });
      setShowMarkPaidModal(false);
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error);
      setMarkPaidError(error instanceof Error ? error.message : 'Failed to mark invoice as paid');
    } finally {
      setIsMarkingPaid(false);
    }
  };
  // Handle void invoice
  const handleVoidInvoice = async () => {
    if (!id) return;
    setIsVoiding(true);
    try {
      await callBillingAPI('voidInvoice', {
        id: id
      });
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Failed to void invoice:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to void invoice'
      });
    } finally {
      setIsVoiding(false);
    }
  };
  // Fetch payments for the invoice
  const fetchPayments = async () => {
    if (!id) return;
    setIsLoadingPayments(true);
    setPaymentsError(null);
    try {
      const response = await callBillingAPI('getPayments', {
        invoice_id: id
      });
      // Handle different response structures
      let paymentsList: any[] = [];
      if (Array.isArray(response)) {
        paymentsList = response;
      } else if (response.payments && Array.isArray(response.payments)) {
        paymentsList = response.payments;
      } else if (response.data && Array.isArray(response.data)) {
        paymentsList = response.data;
      }
      setPayments(paymentsList);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      setPaymentsError(error instanceof Error ? error.message : 'Failed to load payments');
    } finally {
      setIsLoadingPayments(false);
    }
  };
  // Open payments modal and fetch payments
  const openPaymentsModal = () => {
    setShowPaymentsModal(true);
    fetchPayments();
  };
  // Format payment date
  const formatPaymentDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const existingInvoice = isExistingInvoice && id ? getInvoiceById(id) : null;
  // Navigate back to invoices list with preserved filters
  const handleBackToInvoices = () => {
    const savedFilters = sessionStorage.getItem(INVOICE_FILTERS_STORAGE_KEY);
    if (savedFilters) {
      navigate(`/invoices?${savedFilters}`);
    } else {
      navigate('/invoices');
    }
  };
  const statusOptions = [{
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'quote',
    label: 'Quote'
  }, {
    value: 'sent',
    label: 'Sent'
  }, {
    value: 'paid',
    label: 'Paid'
  }, {
    value: 'approved',
    label: 'Approved'
  }, {
    value: 'refunded',
    label: 'Refunded'
  }, {
    value: 'voided',
    label: 'Voided'
  }];
  // Payment terms options (days until overdue)
  const paymentTermsOptions = [{
    value: '0',
    label: 'Due on Receipt'
  }, {
    value: '1',
    label: '1 day'
  }, {
    value: '2',
    label: '2 days'
  }, {
    value: '3',
    label: '3 days'
  }, {
    value: '5',
    label: '5 days'
  }, {
    value: '10',
    label: '10 days'
  }, {
    value: '15',
    label: '15 days'
  }, {
    value: '30',
    label: '30 days'
  }];
  // Line items table columns for viewing
  const lineItemColumns = [{
    key: 'description',
    header: 'Description',
    render: (item: LineItemFormData) => <span className="font-medium text-gray-900" data-id="element-1302">{item.description}</span>
  }, {
    key: 'quantity',
    header: 'Qty',
    render: (item: LineItemFormData) => item.quantity
  }, {
    key: 'price',
    header: 'Price',
    render: (item: LineItemFormData) => formatCurrency(item.price)
  }, {
    key: 'amount',
    header: 'Amount',
    render: (item: LineItemFormData) => formatCurrency(item.quantity * item.price)
  }, {
    key: 'taxable',
    header: 'Taxable',
    render: (item: LineItemFormData) => <Badge variant={item.isTaxable ? 'success' : 'default'} data-id="element-1303">
          {item.isTaxable ? 'Yes' : 'No'}
        </Badge>
  }];
  // Loading state for editing
  if (isExistingInvoice && isLoadingInvoice) {
    return <div className="space-y-6" data-id="element-1304">
        <div className="flex items-center gap-4" data-id="element-1305">
          <Button variant="ghost" onClick={handleBackToInvoices} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1307" />} data-id="element-1306">
            Back
          </Button>
        </div>
        <Card data-id="element-1308">
          <div className="p-12 text-center" data-id="element-1309">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1310"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-1311">Loading invoice...</p>
          </div>
        </Card>
      </div>;
  }
  // Error state for editing
  if (isExistingInvoice && loadError) {
    return <div className="space-y-6" data-id="element-1312">
        <div className="flex items-center gap-4" data-id="element-1313">
          <Button variant="ghost" onClick={handleBackToInvoices} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1315" />} data-id="element-1314">
            Back
          </Button>
        </div>
        <Card data-id="element-1316">
          <div className="p-12 text-center" data-id="element-1317">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-1318">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-1319" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-1320">
              Failed to load invoice
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1321">{loadError}</p>
            <Button size="sm" onClick={() => window.location.reload()} data-id="element-1322">
              Retry
            </Button>
          </div>
        </Card>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1323">
      {/* Page Header */}
      <div className="flex items-center gap-4" data-id="element-1324">
        <Button variant="ghost" onClick={handleBackToInvoices} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1326" />} data-id="element-1325">
          Back
        </Button>
        <div className="flex-1" data-id="element-1327">
          <div className="flex items-center gap-3" data-id="element-1328">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1329">
              {isExistingInvoice ? isEditMode ? `Edit Invoice #${invoiceNumber || ''}` : invoiceNumber ? `Invoice #${invoiceNumber}` : 'Invoice Details' : 'New Invoice'}
            </h1>
            {isExistingInvoice && getStatusBadge(status)}
          </div>
        </div>
        {isExistingInvoice && invoiceLink && <a href={invoiceLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors" data-id="element-1330">
            <ExternalLinkIcon className="w-4 h-4" data-id="element-1331" />
            Open Invoice
          </a>}
        {isViewMode && (status === 'sent' || status === 'overdue' || status === 'paid' || status === 'partial_paid' || status === 'voided') && <Button variant="secondary" onClick={openPaymentsModal} leftIcon={<CreditCardIcon className="w-4 h-4" data-id="element-1333" />} data-id="element-1332">
              Payments
            </Button>}
        {isViewMode && existingInvoice && existingInvoice.status === 'quote' && <Button onClick={() => setShowQuoteApprovalModal(true)} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-1335" />} data-id="element-1334">
              Approve Quote
            </Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1336">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6" data-id="element-1337">
          {/* Error message */}
          {errors.submit && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-1338">
              <p className="text-sm text-red-600" data-id="element-1339">{errors.submit}</p>
            </div>}

          {/* Invoice Details */}
          <Card data-id="element-1340">
            <CardHeader title="Invoice Details" data-id="element-1341" />
            {isViewMode ?
          // View mode for existing invoice
          <div className="space-y-4" data-id="element-1342">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1343">
                  <div data-id="element-1344">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1345">
                      Invoice Number
                    </label>
                    <p className="text-gray-900 font-medium" data-id="element-1346">
                      {invoiceNumber || '—'}
                    </p>
                    {invoiceSource && <p className="text-xs text-gray-500 mt-0.5" data-id="element-1347">
                        {invoiceSource}
                      </p>}
                  </div>
                  <div data-id="element-1348">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1349">
                      Customer
                    </label>
                    <div className="flex items-start gap-2" data-id="element-1350">
                      <UserIcon className="w-4 h-4 text-gray-400 mt-0.5" data-id="element-1351" />
                      <div data-id="element-1352">
                        <p className="text-gray-900 font-medium" data-id="element-1353">
                          {customerName || 'Unknown'}
                        </p>
                        {customerOrganization && customerOrganization.trim() !== (customerName || '').trim() && <p className="text-xs text-gray-500" data-id="element-1354">
                              {customerOrganization}
                            </p>}
                        {customerEmail && <p className="text-xs text-gray-500 flex items-center gap-1 mt-1" data-id="element-1355">
                            <MailIcon className="w-3 h-3 text-gray-400" data-id="element-1356" />
                            {customerEmail}
                          </p>}
                        {customerPhone && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5" data-id="element-1357">
                            <PhoneIcon className="w-3 h-3 text-gray-400" data-id="element-1358" />
                            {customerPhone}
                          </p>}
                        {customerAddress && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5" data-id="element-1359">
                            <MapPinIcon className="w-3 h-3 text-gray-400" data-id="element-1360" />
                            {customerAddress}
                          </p>}
                      </div>
                    </div>
                  </div>
                  <div data-id="element-1361">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1362">
                      Created
                    </label>
                    <div className="flex items-center gap-2" data-id="element-1363">
                      <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-1364" />
                      <p className="text-gray-900" data-id="element-1365">
                        {createdDate ? formatDate(createdDate) : '—'}
                      </p>
                    </div>
                  </div>
                  {invoiceDateDisplay && <div data-id="element-1366">
                      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1367">
                        Invoice Date
                      </label>
                      <div className="flex items-center gap-2" data-id="element-1368">
                        <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-1369" />
                        <p className="text-gray-900" data-id="element-1370">
                          {formatDate(invoiceDateDisplay)}
                        </p>
                      </div>
                    </div>}
                  <div data-id="element-1371">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1372">
                      Payment Terms
                    </label>
                    <p className="text-gray-900" data-id="element-1373">
                      {paymentPeriodDays === 0 ? 'Due on Receipt' : `${paymentPeriodDays} ${paymentPeriodDays === 1 ? 'day' : 'days'}`}
                    </p>
                  </div>
                  {sentDate && <div data-id="element-1374">
                      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1375">
                        Sent Date
                      </label>
                      <p className="text-gray-900" data-id="element-1376">{formatDate(sentDate)}</p>
                    </div>}
                  {paymentDate && <div data-id="element-1377">
                      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1378">
                        Payment Date
                      </label>
                      <p className="text-green-600 font-medium" data-id="element-1379">
                        {formatDate(paymentDate)}
                      </p>
                    </div>}
                  {invoicePaymentMethod && <div data-id="element-1380">
                      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1381">
                        Payment Method
                      </label>
                      <p className="text-gray-900 capitalize" data-id="element-1382">
                        {invoicePaymentMethod}
                      </p>
                    </div>}
                  <div data-id="element-1383">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1384">
                      Status
                    </label>
                    {getStatusBadge(status)}
                  </div>
                  {subscriptionId && <div data-id="element-1385">
                      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1386">
                        Subscription
                      </label>
                      <Link to={`/subscriptions/${subscriptionId}`} className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1" data-id="element-1387">
                        <RefreshCwIcon className="w-4 h-4" data-id="element-1388" />
                        {subscriptionNumber || subscriptionId.slice(0, 8)}
                      </Link>
                    </div>}
                  {/* WF Shipping Methods */}
                  {invoiceData?.extra?.wf_ship_methods && invoiceData.extra.wf_ship_methods.length > 0 && <div data-id="element-1389">
                        <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1390">
                          Shipping Methods
                        </label>
                        <p className="text-gray-900" data-id="element-1391">
                          {invoiceData.extra.wf_ship_methods.join(', ')}
                        </p>
                      </div>}
                  {/* WF Order Status Dropdown */}
                  {invoiceSource === 'wf' && selectedCompany?.use_wf_orders === 1 && selectedCompany?.wf_api_info && wfOrderFilters && invoiceData?.extra?.wf_status_id !== undefined && invoiceData?.extra?.wf_status_id !== null && invoiceData?.extra?.wf_order_id && <div className="sm:col-span-2" data-id="element-1392">
                        <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1393">
                          WF Order Status
                        </label>
                        <div className="space-y-3" data-id="element-1394">
                          <div className="flex gap-2" data-id="element-1395">
                            <div className="flex-1" data-id="element-1396">
                              <Select options={getFilteredWfStatusOptions()} value={selectedWfStatusId || invoiceData.extra.wf_status_id} onChange={e => {
                        setSelectedWfStatusId(e.target.value);
                        setWfStatusError(null);
                      }} data-id="element-1397" />
                            </div>
                            <Button variant="secondary" size="sm" onClick={handleUpdateWfStatus} isLoading={isUpdatingWfStatus} disabled={!selectedWfStatusId || selectedWfStatusId === invoiceData.extra.wf_status_id} data-id="element-1398">
                              Update Status
                            </Button>
                          </div>

                          {/* Show textarea when status is changed */}
                          {selectedWfStatusId && selectedWfStatusId !== invoiceData.extra.wf_status_id && <div data-id="element-1399">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1400">
                                  Message{' '}
                                  <span className="text-gray-400" data-id="element-1401">
                                    (optional)
                                  </span>
                                </label>
                                <textarea value={wfStatusMessage} onChange={e => setWfStatusMessage(e.target.value)} rows={3} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 resize-none" placeholder="e.g., Order shipped via FedEx, tracking #123456..." data-id="element-1402" />
                              </div>}

                          {/* Error message */}
                          {wfStatusError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1403">
                              <p className="text-sm text-red-600" data-id="element-1404">
                                {wfStatusError}
                              </p>
                            </div>}
                        </div>
                      </div>}
                </div>
                {invoiceNotes && <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1405">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-1406">
                      Notes
                    </label>
                    <p className="text-gray-900 whitespace-pre-wrap" data-id="element-1407">
                      {invoiceNotes}
                    </p>
                  </div>}
              </div> :
          // Edit mode for new or editable invoice
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1408">
                <CustomerSelect value={customerId} onChange={setCustomerId} error={errors.customer} selectedLabel={customerName} disabled={isCustomerLocked} data-id="element-1409" />
                <DatePicker label="Invoice Date" value={date} onChange={e => setDate(e.target.value)} error={errors.date} data-id="element-1410" />
                <Select label="Payment Terms" options={paymentTermsOptions} value={String(paymentPeriodDays)} onChange={e => setPaymentPeriodDays(parseInt(e.target.value))} helperText="Days until invoice becomes overdue" data-id="element-1411" />
              </div>}
          </Card>

          {/* Line Items */}
          <Card padding={isViewMode ? 'none' : undefined} data-id="element-1412">
            <div className={isViewMode ? 'p-6 border-b border-gray-200' : ''} data-id="element-1413">
              <CardHeader title="Line Items" action={!isViewMode && <Button variant="secondary" size="sm" onClick={addLineItem} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1416" />} data-id="element-1415">
                      Add Item
                    </Button>} data-id="element-1414" />
            </div>
            {errors.lineItems && !isViewMode && <p className="text-sm text-red-600 mb-4" data-id="element-1417">{errors.lineItems}</p>}
            {isViewMode ? <Table columns={lineItemColumns} data={lineItems} keyExtractor={(_, index) => String(index)} emptyMessage="No items" data-id="element-1418" /> : <div className="space-y-4" data-id="element-1419">
                {lineItems.map((item, index) => <LineItemRow key={index} index={index} description={item.description} quantity={item.quantity} price={item.price} jobCost={item.jobCost} isTaxable={item.isTaxable} onDescriptionChange={value => updateLineItem(index, {
              description: value
            })} onQuantityChange={value => updateLineItem(index, {
              quantity: value
            })} onPriceChange={value => updateLineItem(index, {
              price: value
            })} onJobCostChange={value => updateLineItem(index, {
              jobCost: value
            })} onTaxableChange={value => updateLineItem(index, {
              isTaxable: value
            })} onRemove={() => removeLineItem(index)} canRemove={lineItems.length > 1} data-id="element-1420" />)}
              </div>}
          </Card>

          {/* Additional Charges (only for editable invoices) */}
          {!isViewMode && <Card data-id="element-1421">
              <CardHeader title="Additional Charges" data-id="element-1422" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1423">
                <Input label="Shipping Charge" type="number" min="0" step="0.01" value={shippingCharge} onChange={e => setShippingCharge(parseFloat(e.target.value) || 0)} data-id="element-1424" />
                <Input label="Discount" type="number" min="0" step="0.01" value={discount} onChange={e => setDiscount(parseFloat(e.target.value) || 0)} data-id="element-1425" />
                <Input label="Fee" type="number" min="0" step="0.01" value={fee} onChange={e => setFee(parseFloat(e.target.value) || 0)} data-id="element-1426" />
              </div>
            </Card>}

          {/* Extra Settings (only for editable invoices) */}
          {!isViewMode && <Card data-id="element-1427">
              <CardHeader title="Extra Settings" data-id="element-1428" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1429">
                <Input label="Tax Line Label" value={taxLineLabel} onChange={e => setTaxLineLabel(e.target.value)} placeholder={getDefaultTaxLabel()} helperText="Label shown for tax in invoice summary" data-id="element-1430" />
                <Input label="Fee Line Label" value={feeLineLabel} onChange={e => setFeeLineLabel(e.target.value)} placeholder="Fee" helperText="Label shown for fee in invoice summary" data-id="element-1431" />
              </div>
            </Card>}
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-1432">
          {/* Totals */}
          <Card data-id="element-1433">
            <CardHeader title="Invoice Summary" data-id="element-1434" />
            <TotalsPanel subtotal={subtotal} taxAmount={taxAmount} taxRate={isViewMode ? taxableSubtotal > 0 ? storedTax / taxableSubtotal : 0 : taxRate} taxLabel={isViewMode ? storedTaxLabel : taxLineLabel} shippingCharge={shippingCharge} discount={discount} fee={isViewMode ? storedFee : fee} feeLabel={isViewMode ? storedFeeLabel : feeLineLabel} total={total} data-id="element-1435" />
            {isViewMode && paidAmount > 0 && <div className="mt-4 pt-4 border-t border-gray-200 space-y-2" data-id="element-1436">
                <div className="flex justify-between text-sm" data-id="element-1437">
                  <span className="text-gray-500" data-id="element-1438">Paid Amount</span>
                  <span className="text-green-600 font-medium" data-id="element-1439">
                    {formatCurrency(paidAmount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-semibold" data-id="element-1440">
                  <span className="text-gray-900" data-id="element-1441">Balance Due</span>
                  <span className={balanceDue > 0 ? 'text-red-600' : 'text-green-600'} data-id="element-1442">
                    {formatCurrency(balanceDue)}
                  </span>
                </div>
              </div>}
          </Card>

          {/* Send Options (only for editable invoices) */}
          {!isViewMode && <Card data-id="element-1443">
              <CardHeader title="Send Options" data-id="element-1444" />
              <div className="space-y-3" data-id="element-1445">
                <Checkbox label="Send via Email" checked={sendViaEmail} onChange={e => setSendViaEmail(e.target.checked)} data-id="element-1446" />
                <Checkbox label="Send via SMS" checked={sendViaSMS} onChange={e => setSendViaSMS(e.target.checked)} data-id="element-1447" />
                {errors.sendOptions && <p className="text-sm text-red-600" data-id="element-1448">{errors.sendOptions}</p>}
              </div>
            </Card>}

          {/* Send Options (view mode - disabled) */}
          {isViewMode && <Card data-id="element-1449">
              <CardHeader title="Send Options" data-id="element-1450" />
              <div className="space-y-3" data-id="element-1451">
                <Checkbox label="Send via Email" checked={sendViaEmail} disabled data-id="element-1452" />
                <Checkbox label="Send via SMS" checked={sendViaSMS} disabled data-id="element-1453" />
              </div>
            </Card>}

          {/* Re-Send Invoice Button (only for sent or overdue invoices in view mode) */}
          {isViewMode && (status === 'sent' || status === 'overdue') && <Button className="w-full" onClick={() => navigate(`/invoices/${id}/edit`)} leftIcon={<PencilIcon className="w-4 h-4" data-id="element-1455" />} data-id="element-1454">
              Edit Invoice
            </Button>}

          {/* Re-Send Invoice Button (only for sent or overdue invoices in view mode) */}
          {isViewMode && (status === 'sent' || status === 'overdue') && <Button className="w-full" variant="secondary" onClick={openResendModal} leftIcon={<SendIcon className="w-4 h-4" data-id="element-1457" />} data-id="element-1456">
              Re-Send Invoice
            </Button>}

          {/* Mark as Paid Button (only for sent, overdue, or partial_paid invoices in view mode) */}
          {isViewMode && (status === 'sent' || status === 'overdue' || status === 'partial_paid') && <Button className="w-full" onClick={() => {
          setPaidVia('');
          setPaidNotes('');
          setMarkPaidError(null);
          setShowMarkPaidModal(true);
        }} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-1459" />} data-id="element-1458">
                Mark as Paid
              </Button>}

          {/* Void Invoice Button (only for sent or overdue invoices in view mode) */}
          {isViewMode && (status === 'sent' || status === 'overdue') && <Button className="w-full" variant="danger" onClick={handleVoidInvoice} isLoading={isVoiding} leftIcon={<XCircleIcon className="w-4 h-4" data-id="element-1461" />} data-id="element-1460">
              Void Invoice
            </Button>}

          {/* Clone Invoice Button - available for all invoices in view mode */}
          {isExistingInvoice && isViewMode && <Button className="w-full" variant="secondary" onClick={() => {
          navigate('/invoices/new', {
            state: {
              cloneData: {
                customerId,
                customerName,
                lineItems: lineItems.map(item => ({
                  description: item.description,
                  quantity: item.quantity,
                  price: item.price,
                  isTaxable: item.isTaxable
                })),
                shippingCharge,
                discount,
                fee,
                paymentPeriodDays,
                sendViaEmail,
                sendViaSMS,
                taxLineLabel,
                feeLineLabel
              }
            }
          });
        }} leftIcon={<CopyIcon className="w-4 h-4" data-id="element-1463" />} data-id="element-1462">
              Clone Invoice
            </Button>}

          {/* Delete Invoice Button - available for all invoices in view mode */}
          {isExistingInvoice && isViewMode && <Button className="w-full" variant="danger" onClick={handleDeleteClick} isLoading={isCheckingPayments} leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1465" />} data-id="element-1464">
              Delete Invoice
            </Button>}

          {/* Actions (only for editable invoices) */}
          {!isViewMode && <Card data-id="element-1466">
              <div className="space-y-3" data-id="element-1467">
                {!isCustomerLocked && <Button className="w-full" onClick={() => handleSave(false)} isLoading={isLoading} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-1469" />} data-id="element-1468">
                    {isExistingInvoice ? 'Save Changes' : 'Save Draft'}
                  </Button>}
                <Button variant={isCustomerLocked ? 'primary' : 'secondary'} className="w-full" onClick={() => handleSave(true)} isLoading={isLoading} leftIcon={<SendIcon className="w-4 h-4" data-id="element-1471" />} data-id="element-1470">
                  Save & Send
                </Button>
                {!isExistingInvoice && <Button variant="secondary" className="w-full" onClick={() => {
              if (!validate()) return;
              setShowSaveAndPayModal(true);
            }} disabled={customerPaymentMethods.length === 0 || isLoadingPaymentMethods} leftIcon={<CreditCardIcon className="w-4 h-4" data-id="element-1473" />} title={customerPaymentMethods.length === 0 ? 'Customer has no stored payment methods' : ''} data-id="element-1472">
                    Save & Pay
                  </Button>}
              </div>
            </Card>}

          {/* Delete Invoice Button (only for draft, future, sent, or overdue invoices in edit mode) */}
          {isEditMode && (status === 'draft' || status === 'future' || status === 'sent' || status === 'overdue') && <Button className="w-full" variant="danger" onClick={handleDeleteClick} isLoading={isCheckingPayments} leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1475" />} data-id="element-1474">
                Delete Invoice
              </Button>}
        </div>
      </div>

      {/* Payment Modal */}
      {existingInvoice && existingInvoice.status !== 'quote' && <PaymentModal invoice={existingInvoice} isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} data-id="element-1476" />}

      {/* Quote Approval Modal */}
      {existingInvoice && existingInvoice.status === 'quote' && <QuoteApprovalModal invoice={existingInvoice} lineItems={calculatedLineItems} isOpen={showQuoteApprovalModal} onClose={() => setShowQuoteApprovalModal(false)} onApproved={() => {
      setShowQuoteApprovalModal(false);
      navigate('/invoices');
    }} data-id="element-1477" />}

      {/* Mark as Paid Modal */}
      <Modal isOpen={showMarkPaidModal} onClose={() => {
      setShowMarkPaidModal(false);
      setPaidVia('');
      setPaidNotes('');
      setMarkPaidError(null);
    }} title="Mark Invoice as Paid" size="sm" data-id="element-1478">
        <div className="space-y-4" data-id="element-1479">
          <p className="text-sm text-gray-600" data-id="element-1480">
            How was this invoice paid? Select a payment method and add any
            notes.
          </p>

          {/* Payment Method */}
          <div data-id="element-1481">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1482">
              Payment Method <span className="text-red-500" data-id="element-1483">*</span>
            </label>
            <div className="space-y-2" data-id="element-1484">
              {[{
              value: 'check',
              label: 'Check'
            }, {
              value: 'cash',
              label: 'Cash'
            }, {
              value: 'zelle',
              label: 'Zelle'
            }].map(option => <button key={option.value} type="button" onClick={() => {
              setPaidVia(option.value);
              setMarkPaidError(null);
            }} className={`w-full flex items-center gap-3 p-3 border rounded-lg transition-colors text-left ${paidVia === option.value ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-gray-300 hover:border-gray-400 bg-white'}`} data-id="element-1485">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paidVia === option.value ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`} data-id="element-1486">
                    {paidVia === option.value && <CheckCircleIcon className="w-3 h-3 text-white" data-id="element-1487" />}
                  </div>
                  <span className="font-medium text-gray-900" data-id="element-1488">
                    {option.label}
                  </span>
                </button>)}
            </div>
          </div>

          {/* Notes */}
          <div data-id="element-1489">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1490">
              Notes <span className="text-gray-400" data-id="element-1491">(optional)</span>
            </label>
            <textarea value={paidNotes} onChange={e => setPaidNotes(e.target.value)} rows={3} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 resize-none" placeholder="e.g. Check #1234, received on..." data-id="element-1492" />
          </div>

          {markPaidError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1493">
              <p className="text-sm text-red-600" data-id="element-1494">{markPaidError}</p>
            </div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-1495">
            <Button variant="secondary" onClick={() => {
            setShowMarkPaidModal(false);
            setPaidVia('');
            setPaidNotes('');
            setMarkPaidError(null);
          }} disabled={isMarkingPaid} data-id="element-1496">
              Cancel
            </Button>
            <Button onClick={handleMarkAsPaid} isLoading={isMarkingPaid} disabled={!paidVia} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-1498" />} data-id="element-1497">
              Confirm Payment
            </Button>
          </div>
        </div>
      </Modal>

      {/* Re-Send Invoice Modal */}
      <Modal isOpen={showResendModal} onClose={() => setShowResendModal(false)} title="Re-Send Invoice" size="sm" data-id="element-1499">
        <div className="space-y-4" data-id="element-1500">
          <p className="text-sm text-gray-600" data-id="element-1501">
            Select how you would like to re-send this invoice to the customer.
          </p>
          <div className="space-y-3" data-id="element-1502">
            <Checkbox label="Send via Email" checked={resendViaEmail} onChange={e => setResendViaEmail(e.target.checked)} data-id="element-1503" />
            <Checkbox label="Send via SMS" checked={resendViaSMS} onChange={e => setResendViaSMS(e.target.checked)} data-id="element-1504" />
          </div>
          {resendError && <p className="text-sm text-red-600" data-id="element-1505">{resendError}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-1506">
            <Button variant="secondary" onClick={() => setShowResendModal(false)} disabled={isResending} data-id="element-1507">
              Cancel
            </Button>
            <Button onClick={handleResendInvoice} isLoading={isResending} leftIcon={<SendIcon className="w-4 h-4" data-id="element-1509" />} data-id="element-1508">
              Re-Send
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Invoice Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => {
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      setDeleteError(null);
      setDeleteBlocked(false);
    }} title="Delete Invoice" size="sm" data-id="element-1510">
        <div className="space-y-4" data-id="element-1511">
          {deleteBlocked ? <>
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-1512">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1513" />
                <div data-id="element-1514">
                  <p className="text-sm font-medium text-red-900" data-id="element-1515">
                    Cannot delete this invoice
                  </p>
                  <p className="text-sm text-red-700 mt-1" data-id="element-1516">
                    This invoice has payment records attached and cannot be
                    deleted. You may void the invoice instead.
                  </p>
                </div>
              </div>
              <div className="flex justify-end pt-4 border-t border-gray-200" data-id="element-1517">
                <Button variant="secondary" onClick={() => {
              setShowDeleteModal(false);
              setDeleteBlocked(false);
            }} data-id="element-1518">
                  Close
                </Button>
              </div>
            </> : <>
              <p className="text-gray-600" data-id="element-1519">
                This will permanently delete invoice{' '}
                <span className="font-semibold text-gray-900" data-id="element-1520">
                  #{invoiceNumber || 'this invoice'}
                </span>
                . This action cannot be undone.
              </p>
              <div data-id="element-1521">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1522">
                  Type <span className="font-bold text-red-600" data-id="element-1523">DELETE</span> to
                  confirm
                </label>
                <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} className={`w-full px-4 py-2.5 border rounded-lg text-gray-900 transition-colors focus:ring-2 focus:ring-red-500 focus:border-red-500 ${deleteConfirmText === 'DELETE' ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} placeholder="DELETE" autoComplete="off" data-id="element-1524" />
              </div>
              {deleteError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1525">
                  <p className="text-sm text-red-600" data-id="element-1526">{deleteError}</p>
                </div>}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-1527">
                <Button variant="secondary" onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
              setDeleteError(null);
            }} disabled={isDeleting} data-id="element-1528">
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDeleteInvoice} isLoading={isDeleting} disabled={deleteConfirmText !== 'DELETE'} leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1530" />} data-id="element-1529">
                  Delete
                </Button>
              </div>
            </>}
        </div>
      </Modal>

      {/* Payments Modal */}
      <Modal isOpen={showPaymentsModal} onClose={() => setShowPaymentsModal(false)} title="Payment History" size="lg" data-id="element-1531">
        <div className="space-y-4" data-id="element-1532">
          {isLoadingPayments ? <div className="p-8 text-center" data-id="element-1533">
              <div className="inline-block w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1534"></div>
              <p className="mt-3 text-sm text-gray-500" data-id="element-1535">Loading payments...</p>
            </div> : paymentsError ? <div className="p-6 text-center" data-id="element-1536">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-red-100 rounded-full mb-3" data-id="element-1537">
                <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-1538" />
              </div>
              <p className="text-sm text-gray-900 font-medium mb-1" data-id="element-1539">
                Failed to load payments
              </p>
              <p className="text-sm text-gray-500 mb-3" data-id="element-1540">{paymentsError}</p>
              <Button size="sm" onClick={fetchPayments} data-id="element-1541">
                Retry
              </Button>
            </div> : payments.length === 0 ? <div className="p-8 text-center" data-id="element-1542">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3" data-id="element-1543">
                <CreditCardIcon className="w-6 h-6 text-gray-400" data-id="element-1544" />
              </div>
              <p className="text-sm text-gray-500" data-id="element-1545">
                No payments recorded for this invoice.
              </p>
            </div> : <div className="divide-y divide-gray-200" data-id="element-1546">
              {payments.map((payment, index) => <div key={index} className="py-4 first:pt-0 last:pb-0" data-id="element-1547">
                  <div className="flex items-center justify-between" data-id="element-1548">
                    <div className="flex items-center gap-3" data-id="element-1549">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${payment.status === 'success' ? 'bg-green-100' : 'bg-red-100'}`} data-id="element-1550">
                        <CreditCardIcon className={`w-5 h-5 ${payment.status === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-1551" />
                      </div>
                      <div data-id="element-1552">
                        <div className="flex items-center gap-2" data-id="element-1553">
                          <span className="font-medium text-gray-900" data-id="element-1554">
                            {payment.card_brand || 'Card'} ••••{' '}
                            {payment.card_last_digits || '****'}
                          </span>
                          <Badge variant={payment.status === 'success' ? 'success' : 'danger'} data-id="element-1555">
                            {payment.status === 'success' ? 'Success' : 'Failed'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500" data-id="element-1556">
                          {payment.card_exp ? `Exp: ${payment.card_exp}` : ''}
                          {payment.created ? ` • ${formatPaymentDate(payment.created)}` : ''}
                        </p>
                      </div>
                    </div>
                    <span className={`text-lg font-semibold ${payment.status === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-1557">
                      {formatCurrency(payment.amount || 0)}
                    </span>
                  </div>
                </div>)}
            </div>}
          <div className="flex justify-end pt-4 border-t border-gray-200" data-id="element-1558">
            <Button variant="secondary" onClick={() => setShowPaymentsModal(false)} data-id="element-1559">
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* WF Order Status Update Modal */}
      <Modal isOpen={showWfStatusModal} onClose={() => {
      setShowWfStatusModal(false);
      setWfStatusMessage('');
      setWfStatusError(null);
    }} title="Update WF Order Status" size="sm" data-id="element-1560">
        <div className="space-y-4" data-id="element-1561">
          <p className="text-sm text-gray-600" data-id="element-1562">
            Update the order status in Website Forge. You can optionally add a
            message that will be visible in the order history.
          </p>

          <div data-id="element-1563">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1564">
              New Status
            </label>
            <Select options={getFilteredWfStatusOptions()} value={selectedWfStatusId || invoiceData?.extra?.wf_status_id || ''} onChange={e => setSelectedWfStatusId(e.target.value)} data-id="element-1565" />
          </div>

          <div data-id="element-1566">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1567">
              Message <span className="text-gray-400" data-id="element-1568">(optional)</span>
            </label>
            <textarea value={wfStatusMessage} onChange={e => setWfStatusMessage(e.target.value)} rows={3} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 resize-none" placeholder="e.g., Order shipped via FedEx, tracking #123456..." data-id="element-1569" />
          </div>

          {wfStatusError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1570">
              <p className="text-sm text-red-600" data-id="element-1571">{wfStatusError}</p>
            </div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-1572">
            <Button variant="secondary" onClick={() => {
            setShowWfStatusModal(false);
            setWfStatusMessage('');
            setWfStatusError(null);
          }} disabled={isUpdatingWfStatus} data-id="element-1573">
              Cancel
            </Button>
            <Button onClick={handleUpdateWfStatus} isLoading={isUpdatingWfStatus} disabled={!selectedWfStatusId} leftIcon={<RefreshCwIcon className="w-4 h-4" data-id="element-1575" />} data-id="element-1574">
              Update
            </Button>
          </div>
        </div>
      </Modal>

      {/* Save & Pay Modal */}
      <Modal isOpen={showSaveAndPayModal} onClose={() => setShowSaveAndPayModal(false)} title="Save & Pay Invoice" size="sm" data-id="element-1576">
        <div className="space-y-4" data-id="element-1577">
          <p className="text-sm text-gray-600" data-id="element-1578">
            Select a payment method to charge for this invoice. The invoice will
            be created and immediately paid.
          </p>

          <div data-id="element-1579">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1580">
              Payment Method
            </label>
            <div className="space-y-2" data-id="element-1581">
              {customerPaymentMethods.map(method => <button key={method.id} type="button" onClick={() => setSelectedPaymentMethodId(method.id)} className={`w-full flex items-center gap-3 p-3 border rounded-lg transition-colors text-left ${selectedPaymentMethodId === method.id ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-gray-300 hover:border-gray-400 bg-white'}`} data-id="element-1582">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethodId === method.id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`} data-id="element-1583">
                    {selectedPaymentMethodId === method.id && <CheckCircleIcon className="w-3 h-3 text-white" data-id="element-1584" />}
                  </div>
                  <div className="flex-1" data-id="element-1585">
                    <span className="font-medium text-gray-900 block" data-id="element-1586">
                      {method.card_brand || 'Card'} ••••{' '}
                      {method.card_last_digits || '****'}
                    </span>
                    {method.card_exp && <span className="text-xs text-gray-500 block" data-id="element-1587">
                        Expires {method.card_exp}
                      </span>}
                  </div>
                </button>)}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-1588">
            <Button variant="secondary" onClick={() => setShowSaveAndPayModal(false)} disabled={isLoading} data-id="element-1589">
              Cancel
            </Button>
            <Button onClick={() => {
            setShowSaveAndPayModal(false);
            handleSave(false, {
              paymentMethodId: selectedPaymentMethodId
            });
          }} isLoading={isLoading} disabled={!selectedPaymentMethodId} leftIcon={<CreditCardIcon className="w-4 h-4" data-id="element-1591" />} data-id="element-1590">
              Pay {formatCurrency(total)}
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}